import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { toPortalFailure } from "../lib/errors";
import { sourcedKey, useReadSource, viewRows } from "./readSource";
import type { ClientSiteCount, MonthVisits, ProfileMonth, SearchMonth } from "./statistics";

/**
 * The statistics page's reads, build round 37 (2026-10-11).
 *
 * The counts come through `public.client_site_counts`, because the tables are private: it answers the
 * client's own websites under management, each kind they count, 25 months, `measured`, `checking`
 * and a total that is null while not measured or held. It never answers refused, a placement or a
 * flag's reasons. The visits come straight from `public.analytics_monthly_visits` under the member's
 * row policy, as the overview reads them, with the three figures round 21 added.
 */

export const clientSiteCountsKey = (tenantId: string) => ["portal", "client-site-counts", tenantId] as const;
export const clientSearchMonthsKey = (tenantId: string) => ["portal", "client-search-months", tenantId] as const;
export const statisticsMonthsKey = (tenantId: string) => ["portal", "statistics-months", tenantId] as const;
export const clientProfileMonthsKey = (serviceId: string) => ["portal", "client-profile-months", serviceId] as const;

const KINDS = new Set(["form", "tel", "mailto", "buy"]);

function isRow(value: unknown): value is ClientSiteCount {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.service_id === "string" &&
    typeof row.display_name === "string" &&
    (row.public_url === null || typeof row.public_url === "string") &&
    typeof row.month === "string" &&
    typeof row.kind === "string" &&
    KINDS.has(row.kind) &&
    typeof row.measured === "boolean" &&
    typeof row.checking === "boolean" &&
    (row.total === null || (typeof row.total === "number" && Number.isInteger(row.total) && row.total >= 0))
  );
}

export function useClientSiteCounts(tenantId: string | null): UseQueryResult<ClientSiteCount[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, clientSiteCountsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        const rows = await viewRows<unknown>("admin_as_client_site_counts", { p_tenant_id: source.tenantId });
        if (!rows.every(isRow)) {
          throw toPortalFailure(new Error("PORTAL_UNEXPECTED"));
        }
        return rows;
      }
      const { data, error } = await getSupabaseBrowserClient().rpc("client_site_counts", { p_tenant_id: tenantId as string });
      if (error) {
        throw toPortalFailure(error);
      }
      const value: unknown = data;
      if (!Array.isArray(value) || !value.every(isRow)) {
        throw toPortalFailure(new Error("PORTAL_UNEXPECTED"));
      }
      return value;
    }
  });
}

function readSources(value: unknown): MonthVisits["sources"] {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) {
      return [];
    }
    const { host, visits } = entry as { host?: unknown; visits?: unknown };
    if ((host !== null && typeof host !== "string") || typeof visits !== "number" || visits < 0) {
      return [];
    }
    return [{ host: host as string | null, visits }];
  });
}

export function useStatisticsMonths(tenantId: string | null): UseQueryResult<MonthVisits[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, statisticsMonthsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        const rows = await viewRows<{
          analytics_connection_id: string;
          month: string;
          visits: number;
          mobile_share: number | null;
          speed_rating: string | null;
          sources: unknown;
        }>("admin_as_client_analytics_monthly_visits", { p_tenant_id: source.tenantId });
        return rows.map((row) => ({
          analytics_connection_id: row.analytics_connection_id,
          month: row.month,
          visits: row.visits,
          mobile_share: row.mobile_share,
          speed_rating: row.speed_rating,
          sources: readSources(row.sources)
        }));
      }
      const { data, error } = await getSupabaseBrowserClient()
        .from("analytics_monthly_visits")
        .select("analytics_connection_id, month, visits, mobile_share, speed_rating, sources")
        .order("month", { ascending: false });
      if (error) {
        throw error;
      }
      return (data ?? []).map((row) => ({
        analytics_connection_id: row.analytics_connection_id,
        month: row.month,
        visits: row.visits,
        mobile_share: row.mobile_share,
        speed_rating: row.speed_rating,
        sources: readSources(row.sources)
      }));
    }
  });
}

function isSearchRow(value: unknown): value is SearchMonth {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  const count = (n: unknown) => typeof n === "number" && Number.isInteger(n) && n >= 0;
  return (
    typeof row.service_id === "string" &&
    typeof row.month === "string" &&
    count(row.clicks) &&
    count(row.impressions) &&
    (row.position === null || (typeof row.position === "number" && row.position >= 1)) &&
    Array.isArray(row.top_queries) &&
    row.top_queries.every(
      (query) => typeof query === "object" && query !== null && typeof (query as { key?: unknown }).key === "string" && count((query as { clicks?: unknown }).clicks)
    )
  );
}

/**
 * The client's Search Console months, build round 38 (2026-10-11), through `public.client_search_months`:
 * the client's own websites under management, each stored month's clicks, impressions, Google's average
 * position and the top ten search words with their clicks. PostgREST renders `numeric` as a number.
 */
export function useClientSearchMonths(tenantId: string | null): UseQueryResult<SearchMonth[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, clientSearchMonthsKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        const rows = await viewRows<unknown>("admin_as_client_search_months", { p_tenant_id: source.tenantId });
        if (!rows.every(isSearchRow)) {
          throw toPortalFailure(new Error("PORTAL_UNEXPECTED"));
        }
        return rows;
      }
      const { data, error } = await getSupabaseBrowserClient().rpc("client_search_months", { p_tenant_id: tenantId as string });
      if (error) {
        throw toPortalFailure(error);
      }
      const value: unknown = data;
      if (!Array.isArray(value) || !value.every(isSearchRow)) {
        throw toPortalFailure(new Error("PORTAL_UNEXPECTED"));
      }
      return value;
    }
  });
}

/** A refusal from the live Business Profile read, with the Worker's own code. */
export class ProfileReadFailure extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = "ProfileReadFailure";
  }
}

function isProfileMonth(value: unknown): value is ProfileMonth {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  const figure = (n: unknown) => n === null || (typeof n === "number" && Number.isInteger(n) && n >= 0);
  return (
    typeof row.month === "string" &&
    /^\d{4}-\d{2}$/.test(row.month) &&
    figure(row.calls) &&
    figure(row.website_clicks) &&
    figure(row.directions) &&
    figure(row.views)
  );
}

/**
 * The client's Business Profile months, build round 47 (2026-10-27), read live through the Worker's
 * `/api/integrations/google/profile/read`: up to 24 months before this one to this one (thirteen until build
 * round 53, 2026-10-31), the four figures each, a month Google had no day for as null. Decision 0614: Google's terms forbid keeping them, so they are read from
 * Google in that request and held only in this page's memory, never written to any storage. In "Visa
 * som kund" the Worker asks Petrovic's gateway (`view: true`), never a session as the client.
 *
 * A service with no location answers `PORTAL_BUSINESS_PROFILE_NOT_LINKED`, which is not a failure: the
 * hook resolves to null, and the page shows no section for it. Since build round 53 (2026-10-31) so does
 * `PORTAL_GOOGLE_PROFILE_NOT_CONNECTED`: Petrovic has no connection, as after "Koppla från", which opens
 * no alert, so the client is not told that one is open. A broken connection (`invalid_grant`) does open
 * one, and stays a failure.
 */
export function useClientProfileMonths(serviceId: string | null): UseQueryResult<ProfileMonth[] | null> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, clientProfileMonthsKey(serviceId ?? "none")),
    enabled: Boolean(serviceId),
    queryFn: async () => {
      const { data } = await getSupabaseBrowserClient().auth.getSession();
      const token = data.session?.access_token;
      if (!token) {
        throw new ProfileReadFailure("PORTAL_UNAUTHENTICATED");
      }

      let response: Response;
      try {
        response = await fetch("/api/integrations/google/profile/read", {
          method: "POST",
          headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ service_id: serviceId, view: source.kind === "admin" }),
          credentials: "same-origin"
        });
      } catch {
        throw new ProfileReadFailure("PORTAL_OFFLINE");
      }

      if (!response.ok) {
        const failure = (await response.json().catch(() => null)) as { error?: string } | null;
        const code = failure?.error ?? "gateway_error";
        if (code === "PORTAL_BUSINESS_PROFILE_NOT_LINKED" || code === "PORTAL_GOOGLE_PROFILE_NOT_CONNECTED") {
          return null;
        }
        throw new ProfileReadFailure(code);
      }

      const body = (await response.json().catch(() => null)) as { months?: unknown } | null;
      const months = body?.months;
      if (!Array.isArray(months) || !months.every(isProfileMonth)) {
        throw new ProfileReadFailure("PORTAL_UNEXPECTED");
      }
      return months;
    }
  });
}
